import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  Box,
} from '@chakra-ui/react';
import api from '../../services/api';

const EmailDraftModal = ({
  isOpen,
  onClose,
  onGenerate, // Callback to trigger generation in parent
}) => {
  const [userInstructions, setUserInstructions] = useState('');
  const [senderFirstName, setSenderFirstName] = useState('');
  const [senderLastName, setSenderLastName] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const toast = useToast();

  // Fetch user profile when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);

  const fetchUserProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await api.get('/api/auth/users/me');
      const user = response.data;
      setSenderFirstName(user.first_name || '');
      setSenderLastName(user.last_name || '');
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast({
        title: 'Profile Load Error',
        description: 'Could not load your profile. Please enter your name manually.',
        status: 'warning',
        duration: 3000,
      });
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleGenerate = () => {
    // Validate inputs
    if (!senderFirstName.trim() || !senderLastName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter your first and last name',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Close modal immediately and trigger generation in parent
    const draftingParams = {
      sender_first_name: senderFirstName.trim(),
      sender_last_name: senderLastName.trim(),
      user_instructions: userInstructions.trim(),
    };
    
    setUserInstructions('');
    onClose();
    
    // Trigger generation in parent (after modal closes)
    onGenerate?.(draftingParams);
  };

  const handleClose = () => {
    setUserInstructions('');
    onClose();
  };

  const handleKeyDown = (e) => {
    // Shift+Enter for new line, Enter alone submits if all required fields filled
    if (e.key === 'Enter' && !e.shiftKey) {
      if (senderFirstName.trim() && senderLastName.trim()) {
        e.preventDefault();
        handleGenerate();
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="#1A202C" color="white" maxW="600px">
        <ModalHeader>Draft Emails for Contacts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoadingProfile ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Spinner size="lg" color="blue.400" />
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.400">
                AI will draft personalized emails for each contact based on their profile
                and company information.
              </Text>

              <HStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">First Name</FormLabel>
                  <Input
                    placeholder="Your first name"
                    value={senderFirstName}
                    onChange={(e) => setSenderFirstName(e.target.value)}
                    bg="#2D3748"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'gray.500' }}
                    _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Last Name</FormLabel>
                  <Input
                    placeholder="Your last name"
                    value={senderLastName}
                    onChange={(e) => setSenderLastName(e.target.value)}
                    bg="#2D3748"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'gray.500' }}
                    _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Instructions (Optional)</FormLabel>
                <Textarea
                  placeholder="Example: Focus on how our product can help with their recent expansion. Keep tone professional but friendly."
                  value={userInstructions}
                  onChange={(e) => setUserInstructions(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={6}
                  bg="#2D3748"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'gray.500' }}
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
                />
              </FormControl>

              <Text fontSize="xs" color="gray.500">
                💡 Tip: Preview generation (5 samples) is free. After reviewing, you can approve 
                or iterate before drafting emails for all contacts.
              </Text>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleGenerate}
            isDisabled={isLoadingProfile || !senderFirstName.trim() || !senderLastName.trim()}
          >
            Generate Preview
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmailDraftModal;

